/**
 * Convert a value to a percentage format.
 *
 * @param {(string|number)} value - The value to convert.
 * @param {boolean} [roundUp=true] - Whether to round up the value.
 * @return {number} The value converted to a percentage format.
 */
export function makePercentage(value, roundUp) {
  roundUp = roundUp === false ? false : true
  value = parseFloat(value)
  value = isNaN(value) ? 0 : value
  return roundUp ? Math.round(value) : value
}

/**
 * Rounds a given value to the nearest hundredth, taking into account floating point precision issues.
 *
 * The use of `Number.EPSILON` is to ensure that when the value is very close to a rounding boundary (e.g., .9999999999),
 * it is adjusted slightly upwards by the smallest possible amount (about 2.220446049250313e-16) to tip it over the edge
 * to the next number, which is then rounded correctly. This adjustment helps to mitigate issues caused by the way
 * floating point numbers are represented in JavaScript, which can lead to rounding errors for values very close to
 * the rounding boundary.
 *
 * @param {number} value - The real number to be rounded.
 * @return {number} - The rounded number with 2 decimal places.
 */
export function epsilonRound(value) {
  if (value === null || value === undefined || value === '') {
    return -1
  }

  if (typeof value === 'string') {
    value = Number(value)

    if (isNaN(value)) {
      return -1
    }
  }

  return Math.round((value + Number.EPSILON) * 100) / 100
}

/**
 * Converts meters to kilometers.
 *
 * @param {number} meters - The length in meters to be converted.
 * @return {number} The rounded (2 decimals) length converted to kilometers.
 */
export function metersToKilometers(meters) {
  return parseFloat((meters / 1000).toFixed(2))
}

/**
 * Converts kilometers to meters, rounded to 2 decimals
 * @param {number} kilometers - The length in kilometers to be converted
 * @returns {number|null} The length converted to meters, or null if the input is invalid
 */
export function kilometersToMeters(kilometers) {
  if (typeof kilometers !== 'number' || isNaN(kilometers)) {
    console.error('Invalid input: kilometers must be a number')
    return null
  }

  return parseFloat((kilometers * 1000).toFixed(2))
}
